/**
 * Callback URLs for Better Auth social sign-in.
 *
 * These MUST be absolute. The final redirect after the OAuth round trip is
 * issued by the auth server on auth.bikendure.com, so a bare path like
 * "/video-upload" would resolve against *that* origin and strand the user.
 */

export type AuthMode = 'sign-in' | 'sign-up';

/** Append a query param to a path that may already carry a query string and/or hash. */
export function withParam(url: string, key: string, value: string): string {
  const hashIndex = url.indexOf('#');
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}${hash}`;
}

export interface SocialCallbackUrls {
  callbackURL: string;
  newUserCallbackURL: string;
  errorCallbackURL: string;
}

/**
 * Reject anything that could redirect off-origin. sessionStorage is attacker-writable.
 * Returns the path unchanged when it is a safe same-origin path, or '/' when it is not
 * — callers that cannot navigate to '/' must treat that return value as "no destination".
 */
export function safePath(returnUrl: string): string {
  return returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/';
}

export function buildSocialCallbackUrls(
  origin: string,
  returnUrl: string,
  mode: AuthMode,
): SocialCallbackUrls {
  return {
    callbackURL: `${origin}${withParam(safePath(returnUrl), 'auth', 'google')}`,
    newUserCallbackURL: `${origin}${withParam('/onboarding', 'auth', 'google-new')}`,
    errorCallbackURL: `${origin}/${mode}?error=oauth`,
  };
}
